import { useCallback, useEffect, useState } from 'react';
import { Quill } from 'seagull/patches/PatchedQuill';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import blotStyles from '../TemplateVariableBlot/TemplateVariableBlot.module.scss';
import { isSystemVariable, normalizeVariableLabel } from '../TemplateVariables.utils';
import { useHasTemplateVariablesProvider, useTemplateVariables } from '../TemplateVariablesContext';
import { useInsertVariableIntoQuill } from './useInsertVariableIntoQuill';
import { useQuillKeyboardTrigger } from './useQuillKeyboardTrigger';
import { useVariablePicker } from './useVariablePicker';
import '../TemplateVariableBlot/TemplateVariableBlot';
export function useRichTextVariableIntegration(editorElRef) {
    let config = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    var _config_keyboardEnabled, _ref;
    const { TEMPLATE_VARIABLES } = useFeatureFlags();
    const hasProvider = useHasTemplateVariablesProvider();
    const { customVariables, templateVariableValues, setTemplateVariableValue } = useTemplateVariables();
    const { pickerState, openPicker, closePicker } = useVariablePicker();
    const [pendingDefaultValue, setPendingDefaultValue] = useState(null);
    const enabled = !!TEMPLATE_VARIABLES;
    const handleInsertVariable = useInsertVariableIntoQuill(editorElRef, templateVariableValues);
    const keyboardEnabled = enabled && ((_config_keyboardEnabled = config.keyboardEnabled) !== null && _config_keyboardEnabled !== void 0 ? _config_keyboardEnabled : true);
    useQuillKeyboardTrigger(editorElRef, openPicker, handleInsertVariable, keyboardEnabled);
    useEffect(()=>{
        if (!enabled) return;
        const editorEl = editorElRef.current;
        if (!editorEl) return;
        const handleEditorClick = (event)=>{
            const target = event.target;
            const qlEditor = editorEl.querySelector('.ql-editor');
            if (!qlEditor || target !== qlEditor) return;
            const quill = Quill.find(editorEl);
            if (!quill) return;
            const length = quill.getLength();
            if (length <= 1) return;
            const clickY = event.clientY;
            const lastChild = qlEditor.lastElementChild;
            if (!lastChild) return;
            const lastChildRect = lastChild.getBoundingClientRect();
            if (clickY > lastChildRect.bottom || event.clientX > lastChildRect.right) {
                quill.setSelection(length - 1, 0, 'user');
            }
        };
        editorEl.addEventListener('click', handleEditorClick);
        return ()=>editorEl.removeEventListener('click', handleEditorClick);
    }, [
        editorElRef,
        enabled
    ]);
    useEffect(()=>{
        const editorEl = editorElRef.current;
        if (!editorEl || !enabled) return;
        const handleBlotClick = (event)=>{
            var _target_closest;
            const target = event.target;
            const blotEl = (_target_closest = target.closest('[data-label]')) !== null && _target_closest !== void 0 ? _target_closest : target;
            const label = blotEl.getAttribute('data-label');
            if (!label) return;
            if (isSystemVariable(label)) {
                const quill = Quill.find(editorEl);
                if (quill) {
                    const blot = Quill.find(blotEl);
                    if (blot) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const index = quill.getIndex(blot);
                        quill.setSelection(index, 1, 'user');
                    }
                }
            } else {
                setPendingDefaultValue({
                    label,
                    anchorEl: blotEl
                });
            }
        };
        editorEl.addEventListener('click', handleBlotClick);
        return ()=>editorEl.removeEventListener('click', handleBlotClick);
    }, [
        editorElRef,
        enabled
    ]);
    const handleOpenVariablePicker = useCallback((e)=>{
        openPicker(e.currentTarget, handleInsertVariable);
    }, [
        openPicker,
        handleInsertVariable
    ]);
    const handleSelect = useCallback((label)=>{
        var _pickerState_onInsert;
        (_pickerState_onInsert = pickerState.onInsert) === null || _pickerState_onInsert === void 0 ? void 0 : _pickerState_onInsert.call(pickerState, label);
        closePicker();
        const hasExistingValue = !!templateVariableValues[normalizeVariableLabel(label)];
        if (hasProvider && !isSystemVariable(label) && !hasExistingValue) {
            const editorEl = editorElRef.current;
            const blotNode = editorEl === null || editorEl === void 0 ? void 0 : editorEl.querySelector('[data-label="'.concat(label, '"]'));
            setPendingDefaultValue({
                label,
                anchorEl: blotNode !== null && blotNode !== void 0 ? blotNode : editorEl
            });
        }
    }, [
        pickerState,
        closePicker,
        editorElRef,
        hasProvider,
        templateVariableValues
    ]);
    const handleClose = useCallback(()=>{
        var _pickerState_onDismiss;
        (_pickerState_onDismiss = pickerState.onDismiss) === null || _pickerState_onDismiss === void 0 ? void 0 : _pickerState_onDismiss.call(pickerState);
        closePicker();
    }, [
        pickerState,
        closePicker
    ]);
    const restoreEditorFocus = useCallback(()=>{
        const editorEl = editorElRef.current;
        if (!editorEl) return;
        const quill = Quill.find(editorEl);
        if (quill) {
            setTimeout(()=>quill.focus(), 0);
        }
    }, [
        editorElRef
    ]);
    const handleDefaultValueConfirm = useCallback((value)=>{
        if (pendingDefaultValue) {
            setTemplateVariableValue(pendingDefaultValue.label, value);
            const editorEl = editorElRef.current;
            if (editorEl) {
                editorEl.querySelectorAll('[data-label="'.concat(pendingDefaultValue.label, '"]')).forEach((node)=>node.classList.remove(blotStyles.setup));
            }
        }
        setPendingDefaultValue(null);
    }, [
        pendingDefaultValue,
        setTemplateVariableValue,
        editorElRef
    ]);
    const handleDefaultValueDismiss = useCallback(()=>{
        setPendingDefaultValue(null);
    }, []);
    return {
        enabled,
        toolbarButtonProps: {
            onClick: handleOpenVariablePicker,
            disabled: config.disabled,
            testId: config.testId
        },
        pickerPopoverProps: {
            anchorEl: pickerState.anchorEl,
            customVariables,
            onSelect: handleSelect,
            onClose: handleClose
        },
        defaultValuePopoverProps: {
            anchorEl: (_ref = pendingDefaultValue === null || pendingDefaultValue === void 0 ? void 0 : pendingDefaultValue.anchorEl) !== null && _ref !== void 0 ? _ref : null,
            initialValue: pendingDefaultValue ? templateVariableValues[normalizeVariableLabel(pendingDefaultValue.label)] : undefined,
            onConfirm: handleDefaultValueConfirm,
            onDismiss: handleDefaultValueDismiss,
            onExited: restoreEditorFocus
        }
    };
}
